
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { PlusCircle, Edit, Save, X, Trash } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";

interface EquipmentType {
  id: string;
  name: string;
  base_price: number;
}

export const EquipmentTypesSettings = () => {
  const [equipmentTypes, setEquipmentTypes] = useState<EquipmentType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{name: string, base_price: number}>({ name: "", base_price: 0 });
  const [isAdding, setIsAdding] = useState(false);
  const [newType, setNewType] = useState<{name: string, base_price: number}>({ name: "", base_price: 0 });
  const [isDeleting, setIsDeleting] = useState<Record<string, boolean>>({});
  
  const { toast } = useToast();
  
  useEffect(() => {
    fetchEquipmentTypes();
  }, []);
  
  const fetchEquipmentTypes = async () => {
    try {
      setIsLoading(true);
      
      // Get current user's admin ID (or parent admin if contributor)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      
      const { data, error } = await supabase
        .from('equipment_types')
        .select('*')
        .order('name');
        
      if (error) throw error;
      
      setEquipmentTypes(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load equipment types: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const startEdit = (type: EquipmentType) => {
    setEditingId(type.id);
    setEditValues({ name: type.name, base_price: type.base_price });
  };
  
  const cancelEdit = () => {
    setEditingId(null);
  };
  
  const saveEdit = async () => {
    if (!editingId) return;
    
    try {
      const { error } = await supabase
        .from('equipment_types')
        .update({
          name: editValues.name,
          base_price: editValues.base_price
        })
        .eq('id', editingId);
        
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Equipment type updated successfully",
      });
      
      fetchEquipmentTypes();
      setEditingId(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to update: ${error.message}`,
        variant: "destructive",
      });
    }
  };
  
  const handleDelete = async (id: string) => {
    try {
      setIsDeleting(prev => ({ ...prev, [id]: true }));
      
      const { error } = await supabase
        .from('equipment_types')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Equipment type deleted successfully",
      });
      
      fetchEquipmentTypes();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to delete: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(prev => ({ ...prev, [id]: false }));
    }
  };
  
  const addNewType = async () => {
    if (!newType.name) {
      toast({
        title: "Error",
        description: "Please enter a name for the equipment type",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Get current user's ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      
      const { error } = await supabase
        .from('equipment_types')
        .insert({
          name: newType.name,
          base_price: newType.base_price,
          admin_id: user.id
        });
        
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "New equipment type added",
      });
      
      setNewType({ name: "", base_price: 0 });
      setIsAdding(false);
      fetchEquipmentTypes();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to add equipment type: ${error.message}`,
        variant: "destructive",
      });
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium">Equipment Types</h2>
        {!isAdding ? (
          <Button onClick={() => setIsAdding(true)} size="sm">
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Type
          </Button>
        ) : (
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={() => setIsAdding(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      
      {isAdding && (
        <Card className="p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="col-span-2">
              <Input 
                placeholder="Equipment name"
                value={newType.name}
                onChange={(e) => setNewType({...newType, name: e.target.value})}
              />
            </div>
            <div>
              <Input 
                type="number"
                placeholder="Base price"
                value={newType.base_price.toString()}
                onChange={(e) => setNewType({...newType, base_price: parseFloat(e.target.value) || 0})}
                step="0.01"
                min="0"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={addNewType}>
              Add Equipment Type
            </Button>
          </div>
        </Card>
      )}
      
      {isLoading ? (
        <div className="p-4 text-center">Loading equipment types...</div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Base Price (€)</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {equipmentTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-6 text-muted-foreground">
                    No equipment types found.
                  </TableCell>
                </TableRow>
              ) : (
                equipmentTypes.map((type) => (
                  <TableRow key={type.id}>
                    <TableCell>
                      {editingId === type.id ? (
                        <Input 
                          value={editValues.name}
                          onChange={(e) => setEditValues({...editValues, name: e.target.value})}
                        />
                      ) : (
                        type.name
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === type.id ? (
                        <Input 
                          type="number"
                          value={editValues.base_price.toString()}
                          onChange={(e) => setEditValues({...editValues, base_price: parseFloat(e.target.value) || 0})}
                          step="0.01"
                          min="0"
                        />
                      ) : (
                        `€${type.base_price.toFixed(2)}`
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {editingId === type.id ? (
                        <div className="flex justify-end space-x-2">
                          <Button variant="outline" size="sm" onClick={cancelEdit}>
                            <X className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={saveEdit}>
                            <Save className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end space-x-2">
                          <Button variant="ghost" size="sm" onClick={() => startEdit(type)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete(type.id)}
                            disabled={isDeleting[type.id]}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
